import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo, useState } from 'react';
import { Typography, Box, Paper, Button, Alert, TextField, FormControl, Select, } from '@weave-mui/material';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, MenuItem, Link as MuiLink, } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../api/apiClient';
import { queryKeys } from '../../query/queryClient';
import { TableSkeleton } from '../../components/Loading';
const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const ROWS_PER_PAGE = 20;
function isValidCalendarDate(value) {
    if (!DATE_REGEX.test(value))
        return false;
    const [year, month, day] = value.split('-').map(Number);
    const d = new Date(year, month - 1, day);
    return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}
const CollectionRecordsTab = () => {
    const [dbRefreshDate, setDbRefreshDate] = useState('');
    const [batchId, setBatchId] = useState('');
    const [caseName, setCaseName] = useState('');
    const [submitted, setSubmitted] = useState(null);
    const [page, setPage] = useState(0);
    const [formError, setFormError] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);
    const { data: casesData } = useQuery({
        queryKey: queryKeys.dataFix.cases(),
        queryFn: () => api.dataFix.getCases(),
        staleTime: 60 * 60 * 1000,
    });
    const cases = casesData?.cases ?? [];
    const { data, isFetching, error } = useQuery({
        queryKey: submitted ? queryKeys.dataFix.collectionRecords(submitted) : ['dataFix', 'collectionRecords', 'idle'],
        queryFn: () => api.dataFix.getDataFixCollectionRecords(submitted),
        enabled: !!submitted,
    });
    const records = useMemo(() => data?.records ?? [], [data]);
    const paginatedRecords = useMemo(() => records.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE), [records, page]);
    const handleQuery = () => {
        const trimmedDate = dbRefreshDate.trim();
        const trimmedBatch = batchId.trim();
        if (!isValidCalendarDate(trimmedDate)) {
            setFormError('DB refresh date must be a valid YYYY-MM-DD.');
            return;
        }
        if (!trimmedBatch) {
            setFormError('Batch ID is required.');
            return;
        }
        if (!caseName) {
            setFormError('Case is required.');
            return;
        }
        setFormError(null);
        setDownloadError(null);
        setPage(0);
        setSubmitted({ dbRefreshDate: trimmedDate, batchId: trimmedBatch, caseName });
    };
    const handleDownload = async () => {
        if (!submitted)
            return;
        setDownloadError(null);
        setIsDownloading(true);
        try {
            const { blob, filename } = await api.dataFix.downloadDataFixCollectionRecords(submitted);
            const objectUrl = URL.createObjectURL(blob);
            try {
                const a = document.createElement('a');
                a.href = objectUrl;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            }
            finally {
                URL.revokeObjectURL(objectUrl);
            }
        }
        catch (err) {
            const apiErr = err;
            setDownloadError(apiErr.message || 'Failed to download CSV.');
        }
        finally {
            setIsDownloading(false);
        }
    };
    return (_jsxs(Box, { sx: { p: 3 }, children: [_jsx(Typography, { variant: "h6", sx: { fontWeight: 600, mb: 2 }, children: "Collection Records" }), _jsxs(Paper, { sx: { p: 2, mb: 3 }, children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 640 }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Typography, { sx: { minWidth: 140, textAlign: 'left' }, children: "DB Refresh Date" }), _jsx(TextField, { placeholder: "YYYY-MM-DD", value: dbRefreshDate, onChange: (e) => setDbRefreshDate(e.target.value), size: "small", fullWidth: true })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Typography, { sx: { minWidth: 140, textAlign: 'left' }, children: "Batch ID" }), _jsx(TextField, { value: batchId, onChange: (e) => setBatchId(e.target.value), size: "small", fullWidth: true })] }), _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(Typography, { sx: { minWidth: 140, textAlign: 'left' }, children: "Case" }), _jsx(FormControl, { size: "small", fullWidth: true, children: _jsxs(Select, { value: caseName, displayEmpty: true, onChange: (e) => setCaseName(String(e.target.value)), children: [_jsx(MenuItem, { value: "", disabled: true, children: _jsx("em", { children: "Select a case" }) }), cases.map((c) => (_jsx(MenuItem, { value: c.taskName, children: c.displayName }, c.taskName)))] }) })] }), _jsxs(Box, { sx: { display: 'flex', gap: 2 }, children: [_jsx(Button, { variant: "contained", onClick: handleQuery, disabled: isFetching, children: "Query" }), _jsx(Button, { variant: "outlined", onClick: () => void handleDownload(), disabled: !submitted || isDownloading || isFetching, children: isDownloading ? 'Downloading…' : 'Download CSV' })] })] }), formError && (_jsx(Alert, { severity: "warning", sx: { mt: 2 }, children: formError })), downloadError && (_jsx(Alert, { severity: "error", sx: { mt: 2 }, children: downloadError }))] }), error && (_jsxs(Alert, { severity: "error", sx: { mb: 2 }, children: ["Failed to load records: ", error.message ?? 'Unknown error'] })), isFetching && _jsx(TableSkeleton, { rows: 5 }), !isFetching && submitted && !error && (_jsxs(Paper, { children: [_jsx(TableContainer, { children: _jsxs(Table, { size: "small", children: [_jsx(TableHead, { children: _jsxs(TableRow, { sx: { '& th': { fontWeight: 700 } }, children: [_jsx(TableCell, { children: "Collection ID" }), _jsx(TableCell, { children: "Status" }), _jsx(TableCell, { children: "End Time" })] }) }), _jsxs(TableBody, { children: [records.length === 0 && (_jsx(TableRow, { children: _jsx(TableCell, { colSpan: 3, align: "center", children: _jsx(Typography, { variant: "body2", color: "text.secondary", sx: { py: 3 }, children: "No records found." }) }) })), paginatedRecords.map((r) => (_jsxs(TableRow, { hover: true, children: [_jsx(TableCell, { children: _jsx(MuiLink, { component: RouterLink, to: `/collections/${r.collectionId}`, target: "_blank", rel: "noopener noreferrer", sx: { fontFamily: 'monospace' }, children: r.collectionId }) }), _jsx(TableCell, { children: r.status }), _jsx(TableCell, { children: r.endTime ? new Date(r.endTime).toLocaleString() : '—' })] }, r.sk)))] })] }) }), records.length > 0 && (_jsx(TablePagination, { rowsPerPageOptions: [], component: "div", count: records.length, rowsPerPage: ROWS_PER_PAGE, page: page, onPageChange: (_, newPage) => setPage(newPage) }))] }))] }));
};
export default CollectionRecordsTab;
